import { defineStore } from "pinia";
import { useAuthRepository } from "~/composables/repositories/useAuthRepository";
import type { AuthResult, AuthStatus, User } from "~/types";

const MOCK_SESSION_KEY = "relink:mock-user-id";

export const useSessionStore = defineStore("session", {
  state: () => ({
    blockedReason: "",
    restored: false,
    status: "anonymous" as AuthStatus,
    user: null as User | null
  }),
  getters: {
    isAuthenticated: (state) =>
      state.status === "authenticated" && state.user !== null,
    isAdmin: (state) => state.user?.role === "COMPANYADMIN",
    isParticipant: (state) => state.user?.role === "PARTICIPANT",
    companyId: (state) => state.user?.companyId ?? null,
    userFullName: (state) =>
      state.user
        ? `${state.user.names} ${state.user.paternalSurname}`.trim()
        : ""
  },
  actions: {
    block(reason: string) {
      this.user = null;
      this.blockedReason = reason;
      this.status = "blocked";

      useCookie(MOCK_SESSION_KEY).value = undefined;
    },
    authenticate(result: AuthResult) {
      this.user = result.user;
      this.blockedReason = "";
      this.restored = true;
      this.status = "authenticated";

      const runtimeConfig = useRuntimeConfig();

      if (runtimeConfig.public.MOCK_API_ENABLED !== false) {
        // id:role — restoreSession re-checks the role
        useCookie(MOCK_SESSION_KEY).value =
          `${result.user.id}:${result.user.role}`;
      }
    },
    async logout() {
      const repository = useAuthRepository();
      await repository.logout();
      this.user = null;
      this.blockedReason = "";
      this.status = "anonymous";

      useCookie(MOCK_SESSION_KEY).value = undefined;
    },
    async restoreSession() {
      if (this.restored) {
        return;
      }

      this.restored = true;
      const runtimeConfig = useRuntimeConfig();
      const mockCookieRaw =
        runtimeConfig.public.MOCK_API_ENABLED !== false
          ? useCookie(MOCK_SESSION_KEY).value
          : undefined;
      const [rawId, rawRole] = (mockCookieRaw ?? "").split(":");
      const parsedId = rawId ? Number(rawId) : NaN;
      const mockUserId = Number.isInteger(parsedId) ? parsedId : undefined;

      if (runtimeConfig.public.MOCK_API_ENABLED !== false && !mockUserId) {
        return;
      }

      const repository = useAuthRepository();
      const response = await repository.getCurrentUser(mockUserId);

      // Role must match what the cookie claimed.
      if (
        !response.success ||
        !response.data ||
        (rawRole && response.data.role !== rawRole)
      ) {
        useCookie(MOCK_SESSION_KEY).value = undefined;
        this.user = null;
        this.status = "anonymous";
        return;
      }

      this.user = response.data;
      this.status = "authenticated";
    }
  }
});
